import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@vinoga/db";
import {
  ArrowLeft,
  Save,
  User,
  Building2,
  ShoppingCart,
  MapPin,
  Landmark,
  Newspaper,
  ShieldOff,
  Snowflake,
  Check,
  ChevronsUpDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  PHONE_DIALS,
  splitPhoneIntl,
  joinPhoneIntl,
} from "@/lib/phone-dials";

const sectionClass = "bg-card border border-border rounded-lg p-5 space-y-4";

type CustomerRow = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  customer_group_id: string;
  company_name: string | null;
  company_reg_no: string | null;
  vat_number: string | null;
  marketing_consent: boolean;
  skip_age_verification: boolean;
  chilled_delivery_enabled: boolean;
  bank_transfer_enabled: boolean;
  after_hours_enabled: boolean;
  moneo_cust_code: string | null;
  created_at: string;
  last_login_at: string | null;
};

type CustomerGroup = {
  id: string;
  code: string;
  name: string;
  is_business: boolean;
};

type AddressRow = {
  id: string;
  label: string | null;
  recipient_name: string;
  phone: string;
  street: string;
  city: string;
  postal_code: string;
  country: string;
  is_default_billing: boolean;
  is_default_shipping: boolean;
};

type OrderSummary = {
  id: string;
  order_number: string;
  status: string;
  payment_status: string;
  total_amount: string;
  created_at: string;
};


export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const t = useT();

  const [customer, setCustomer] = useState<CustomerRow | null>(null);
  const [groups, setGroups] = useState<CustomerGroup[]>([]);
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [addresses, setAddresses] = useState<AddressRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Editable form fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dialOpen, setDialOpen] = useState(false);
  const [groupId, setGroupId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyRegNo, setCompanyRegNo] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [skipAgeVerification, setSkipAgeVerification] = useState(false);
  const [chilledDelivery, setChilledDelivery] = useState(false);
  const [bankTransfer, setBankTransfer] = useState(false);
  const [moneoCustCode, setMoneoCustCode] = useState("");
  const [afterHours, setAfterHours] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [c, g, o, a] = await Promise.all([
        supabase.from("customers").select("*").eq("id", id).single(),
        supabase
          .from("customer_groups")
          .select("id, code, name, is_business"),
        supabase
          .from("orders")
          .select("id, order_number, status, payment_status, total_amount, created_at")
          .eq("customer_id", id)
          .order("created_at", { ascending: false })
          .limit(50),
        supabase.from("addresses").select("*").eq("customer_id", id),
      ]);
      if (cancelled) return;
      if (c.error || !c.data) {
        toast({
          title: t("common.notFound", "Not found"),
          description: c.error?.message ?? t("customerDetail.notFound", "Customer not found"),
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
      const cust = c.data as CustomerRow;
      setCustomer(cust);
      setFirstName(cust.first_name ?? "");
      setLastName(cust.last_name ?? "");
      setEmail(cust.email);
      setPhone(cust.phone ?? "");
      setGroupId(cust.customer_group_id);
      setCompanyName(cust.company_name ?? "");
      setCompanyRegNo(cust.company_reg_no ?? "");
      setVatNumber(cust.vat_number ?? "");
      setMarketingConsent(cust.marketing_consent);
      setSkipAgeVerification(cust.skip_age_verification ?? false);
      setChilledDelivery(cust.chilled_delivery_enabled ?? false);
      setBankTransfer(cust.bank_transfer_enabled ?? false);
      setAfterHours(cust.after_hours_enabled ?? false);
      setMoneoCustCode(cust.moneo_cust_code ?? "");
      setGroups((g.data ?? []) as CustomerGroup[]);
      setOrders((o.data ?? []) as OrderSummary[]);
      setAddresses((a.data ?? []) as AddressRow[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t, toast]);

  const save = async () => {
    if (!customer) return;
    if (!email.trim()) {
      toast({
        title: t("customerDetail.emailRequired", "Email is required"),
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("customers")
      .update({
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        email: email.trim().toLowerCase(),
        phone: phone.trim() || null,
        customer_group_id: groupId,
        company_name: companyName.trim() || null,
        company_reg_no: companyRegNo.trim() || null,
        vat_number: vatNumber.trim() || null,
        marketing_consent: marketingConsent,
        skip_age_verification: skipAgeVerification,
        chilled_delivery_enabled: chilledDelivery,
        bank_transfer_enabled: bankTransfer,
        after_hours_enabled: afterHours,
        moneo_cust_code: moneoCustCode.trim() || null,
      })
      .eq("id", customer.id);
    if (error) {
      toast({
        title: t("common.saveFailed", "Save failed"),
        description: error.message,
        variant: "destructive",
      });
      setSaving(false);
      return;
    }
    toast({ title: t("customerDetail.saved", "Customer saved") });
    setSaving(false);
  };

  if (loading || !customer) {
    return (
      <div className="p-4 font-body text-sm text-muted-foreground">
        {t("common.loading", "Loading...")}
      </div>
    );
  }

  const currentGroup = groups.find((g) => g.id === groupId);
  const isBusiness = currentGroup?.is_business ?? false;
  const fullName = [firstName, lastName].filter(Boolean).join(" ") || email;

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/customers")}
            className="p-1 text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="font-display text-xl font-bold text-foreground">{fullName}</h1>
            <p className="font-body text-xs text-muted-foreground">
              {t("customerDetail.customerSince", "Customer since")}{" "}
              {new Date(customer.created_at).toLocaleDateString(undefined, {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </p>
          </div>
        </div>
        <Button size="sm" onClick={save} disabled={saving}>
          <Save size={14} />{" "}
          {saving ? t("common.saving", "Saving…") : t("common.save", "Save")}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* LEFT — profile + business + orders */}
        <div className="space-y-6 xl:col-span-2">
          <section className={sectionClass}>
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <User size={16} className="text-muted-foreground" />{" "}
              {t("customerDetail.profile", "Profile")}
            </h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label={t("customers.colFirstName", "First name")}>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="h-9"
                />
              </Field>
              <Field label={t("customers.colLastName", "Last name")}>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="h-9"
                />
              </Field>
              <Field label={t("customers.colEmail", "Email")}>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-9"
                />
              </Field>
              <Field label={t("customers.colPhone", "Phone")}>
                {/* Dial-code selector (owner 2026-09-25/27) — mirrors the
                    storefront PhoneInput: full country list, searchable by
                    name/ISO/dial. Stored E.164; bare digits = LV. */}
                <div className="flex gap-1.5">
                  <Popover open={dialOpen} onOpenChange={setDialOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        role="combobox"
                        aria-expanded={dialOpen}
                        className="flex h-9 w-28 shrink-0 items-center justify-between rounded-md border border-input bg-background px-2.5 font-body text-sm"
                      >
                        <span>
                          {(() => {
                            const d = splitPhoneIntl(phone).dial;
                            const c = PHONE_DIALS.find((x) => x.dial === d);
                            return `${c?.flag ?? ""} +${d}`;
                          })()}
                        </span>
                        <ChevronsUpDown size={14} className="ml-1 shrink-0 opacity-50" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[280px] p-0" align="start">
                      <Command>
                        <CommandInput
                          placeholder={t(
                            "customerDetail.dialSearch",
                            "Meklēt valsti vai kodu…",
                          )}
                        />
                        <CommandList>
                          <CommandEmpty>
                            {t("customerDetail.dialEmpty", "Nekas netika atrasts.")}
                          </CommandEmpty>
                          <CommandGroup>
                            {PHONE_DIALS.map((c) => (
                              <CommandItem
                                key={c.iso}
                                value={`${c.name} ${c.iso} +${c.dial} ${c.dial}`}
                                onSelect={() => {
                                  setPhone(
                                    joinPhoneIntl(c.dial, splitPhoneIntl(phone).national),
                                  );
                                  setDialOpen(false);
                                }}
                              >
                                <Check
                                  size={14}
                                  className={cn(
                                    "mr-2",
                                    splitPhoneIntl(phone).dial === c.dial
                                      ? "opacity-100"
                                      : "opacity-0",
                                  )}
                                />
                                <span className="mr-2">{c.flag}</span>
                                <span className="flex-1 truncate">{c.name}</span>
                                <span className="text-muted-foreground">+{c.dial}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Input
                    value={splitPhoneIntl(phone).national}
                    onChange={(e) =>
                      setPhone(joinPhoneIntl(splitPhoneIntl(phone).dial, e.target.value))
                    }
                    className="h-9"
                  />
                </div>
              </Field>
              <Field label={t("customerDetail.group", "Customer group")}>
                <Select value={groupId} onValueChange={setGroupId}>
                  <SelectTrigger className="h-9 font-body text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="flex items-center gap-2 border-t border-border pt-4">
              <Switch checked={marketingConsent} onCheckedChange={setMarketingConsent} />
              <label className="flex items-center gap-1 font-body text-sm">
                <Newspaper size={14} className="text-muted-foreground" />
                {t("customerDetail.marketingConsent", "Marketing consent")}
              </label>
            </div>
            <div className="flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
              <Switch
                checked={skipAgeVerification}
                onCheckedChange={setSkipAgeVerification}
                className="mt-0.5"
              />
              <label className="space-y-0.5">
                <span className="flex items-center gap-1 font-body text-sm font-medium">
                  <ShieldOff size={14} className="text-amber-600" />
                  {t("customerDetail.skipAgeVerification", "Skip age verification")}
                </span>
                <span className="block font-body text-xs text-muted-foreground">
                  {t(
                    "customerDetail.skipAgeVerificationHint",
                    "Exempt this customer from Dokobit (Smart-ID / Mobile-ID) age verification at checkout. Use only when identity is confirmed by other means.",
                  )}
                </span>
              </label>
            </div>
            <div className="flex items-start gap-3 rounded-md border border-sky-500/40 bg-sky-500/5 p-3">
              <Switch
                checked={chilledDelivery}
                onCheckedChange={setChilledDelivery}
                className="mt-0.5"
              />
              <label className="space-y-0.5">
                <span className="flex items-center gap-1 font-body text-sm font-medium">
                  <Snowflake size={14} className="text-sky-600" />
                  {t("customerDetail.chilledDelivery", "Saņemt atdzesētu")}
                </span>
                <span className="block font-body text-xs text-muted-foreground">
                  {t(
                    "customerDetail.chilledDeliveryHint",
                    "Rāda šim klientam kasē bezmaksas opciju “Saņemt atdzesētu” (6–8°C, termo iepakojumā). Pēc noklusējuma izslēgta.",
                  )}
                </span>
              </label>
            </div>
            <div className="flex items-start gap-3 rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3">
              <Switch
                checked={bankTransfer}
                onCheckedChange={setBankTransfer}
                className="mt-0.5"
              />
              <label className="space-y-0.5">
                <span className="flex items-center gap-1 font-body text-sm font-medium">
                  <Landmark size={14} className="text-emerald-600" />
                  {t("customerDetail.bankTransfer", "Pēcapmaksa ar bankas pārskaitījumu")}
                </span>
                <span className="block font-body text-xs text-muted-foreground">
                  {t(
                    "customerDetail.bankTransferHint",
                    "Kasē pieejams maksājuma veids “Pēcapmaksa ar bankas pārskaitījumu” — pasūtījums tiek apstiprināts bez Montonio apmaksas un gaida manuālu apmaksas atzīmēšanu. Pēc noklusējuma izslēgta.",
                  )}
                </span>
              </label>
            </div>
            <div className="flex items-start gap-3 rounded-md border border-violet-500/40 bg-violet-500/5 p-3">
              <Switch
                checked={afterHours}
                onCheckedChange={setAfterHours}
                className="mt-0.5"
              />
              <label className="space-y-0.5">
                <span className="flex items-center gap-1 font-body text-sm font-medium">
                  <Landmark size={14} className="text-violet-600" />
                  {t("customerDetail.afterHours", "Pirkumi ārpus atļautā laika (24/7)")}
                </span>
                <span className="block font-body text-xs text-muted-foreground">
                  {t(
                    "customerDetail.afterHoursHint",
                    "Klients drīkst apstiprināt alkohola pasūtījumus arī ārpus P–S 10:00–20:00 / Sv 10:00–18:00. Ieslēdzas automātiski, kad klients noformē pasūtījumu kā juridiska persona (B2B).",
                  )}
                </span>
              </label>
            </div>
            {/* Per-client Moneo customer code (owner 2026-10-08). Empty =
                invoices book under the shared retail code from Moneo
                settings. The code must already exist in Moneo. */}
            <div className="space-y-1 border-t border-border pt-4">
              <Field label={t("customerDetail.moneoCustCode", "Moneo klienta kods")}>
                <Input
                  value={moneoCustCode}
                  onChange={(e) => setMoneoCustCode(e.target.value)}
                  placeholder={t("customerDetail.moneoCustCodePlaceholder", "piem., 31245")}
                  className="h-9 max-w-xs"
                />
              </Field>
              <p className="font-body text-xs text-muted-foreground">
                {t(
                  "customerDetail.moneoCustCodeHint",
                  "Ja norādīts, šī klienta pasūtījumu rēķini Moneo tiek grāmatoti ar šo klienta kodu. Ja tukšs — ar noklusējuma mazumtirdzniecības kodu no Moneo iestatījumiem. Kodam jau jāeksistē Moneo.",
                )}
              </p>
            </div>
          </section>

          {isBusiness && (
            <section className={sectionClass}>
              <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
                <Building2 size={16} className="text-muted-foreground" />{" "}
                {t("customerDetail.business", "Business details")}
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label={t("customerDetail.companyName", "Company name")}>
                  <Input
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="h-9"
                  />
                </Field>
                <Field label={t("customerDetail.companyRegNo", "Registration #")}>
                  <Input
                    value={companyRegNo}
                    onChange={(e) => setCompanyRegNo(e.target.value)}
                    className="h-9"
                  />
                </Field>
                <Field label={t("customerDetail.vatNumber", "VAT number")}>
                  <Input
                    value={vatNumber}
                    onChange={(e) => setVatNumber(e.target.value)}
                    className="h-9"
                  />
                </Field>
              </div>
            </section>
          )}

          <section className={sectionClass}>
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <ShoppingCart size={16} className="text-muted-foreground" />{" "}
              {t("customerDetail.recentOrders", "Recent orders")} ({orders.length})
            </h2>
            {orders.length === 0 ? (
              <p className="font-body text-xs text-muted-foreground">
                {t("customerDetail.noOrders", "No orders yet.")}
              </p>
            ) : (
              <div className="overflow-hidden rounded-md border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("orders.colNumber", "Order #")}</TableHead>
                      <TableHead className="text-center">
                        {t("orders.colStatus", "Status")}
                      </TableHead>
                      <TableHead className="text-center">
                        {t("orders.colPayment", "Payment")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("orders.colTotal", "Total")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("orders.colCreated", "Created")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((o) => (
                      <TableRow
                        key={o.id}
                        className="cursor-pointer"
                        onClick={() => navigate(`/orders/${o.id}`)}
                      >
                        <TableCell className="py-2 font-mono text-xs font-medium">
                          {o.order_number}
                        </TableCell>
                        <TableCell className="py-2 text-center">
                          <Badge variant="secondary">{o.status}</Badge>
                        </TableCell>
                        <TableCell className="py-2 text-center">
                          <Badge variant={o.payment_status === "paid" ? "success" : "secondary"}>
                            {o.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-2 text-right font-medium">
                          €{Number(o.total_amount).toFixed(2)}
                        </TableCell>
                        <TableCell className="py-2 text-right text-muted-foreground">
                          {new Date(o.created_at).toLocaleDateString(undefined, {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>
        </div>

        {/* RIGHT — addresses + meta */}
        <div className="space-y-6">
          <section className={sectionClass}>
            <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <MapPin size={16} className="text-muted-foreground" />{" "}
              {t("customerDetail.addresses", "Addresses")} ({addresses.length})
            </h2>
            {addresses.length === 0 ? (
              <p className="font-body text-xs text-muted-foreground">
                {t("customerDetail.noAddresses", "No saved addresses.")}
              </p>
            ) : (
              <div className="space-y-3">
                {addresses.map((a) => (
                  <div key={a.id} className="rounded-md border border-border p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{a.label ?? a.recipient_name}</div>
                      <div className="flex gap-1">
                        {a.is_default_billing && (
                          <Badge variant="outline" className="text-[10px]">
                            {t("customerDetail.defaultBilling", "Billing")}
                          </Badge>
                        )}
                        {a.is_default_shipping && (
                          <Badge variant="outline" className="text-[10px]">
                            {t("customerDetail.defaultShipping", "Shipping")}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {a.recipient_name}
                      <br />
                      {a.street}
                      <br />
                      {a.postal_code} {a.city}
                      <br />
                      {a.country}
                      <br />
                      {a.phone}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className={sectionClass}>
            <h2 className="font-display text-sm font-bold text-foreground">
              {t("customerDetail.meta", "Meta")}
            </h2>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("customerDetail.lastLogin", "Last login")}
                </span>
                <span>
                  {customer.last_login_at
                    ? new Date(customer.last_login_at).toLocaleDateString()
                    : "—"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("customerDetail.totalOrders", "Total orders")}
                </span>
                <span>{orders.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("customerDetail.totalSpend", "Total spend")}
                </span>
                <span>
                  €
                  {orders
                    .filter((o) => o.payment_status === "paid")
                    .reduce((sum, o) => sum + Number(o.total_amount), 0)
                    .toFixed(2)}
                </span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block font-body text-xs text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
